import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { Page } from "@formant/ui/component/common/Page";
import { VStack } from "@formant/ui/component/common/VStack";

import { Scrollable } from "../common/Scrollable";
import { Switch } from "../common/Switch";
import { TabWorkflowButtons } from "../common/TabWorkflowButtons";
import { Container } from "../Container";
import * as styles from "./DefaultTeleopConfiguration.scss";
import { TeleopViewSelectField } from "./TeleopViewSelectField";

export interface ITeleopConfigurationProps {
    devices?: IDevice[];
    document: IDeviceConfigurationDocument;
    dirty: boolean;
    unsaved?: boolean;
    validation: IValidation;
    saveCopy?: string;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onSave: () => void;
    loading: boolean;
    popup?: boolean;
}

@observer
export class DefaultTeleopConfiguration extends Component<ITeleopConfigurationProps> {
    public render() {
        const {
            devices,
            dirty,
            unsaved,
            document,
            saveCopy,
            onSave,
            loading,
            popup,
        } = this.props;

        const deviceIds = devices?.map(_ => defined(_.id));
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];

        return (
            <Page
                className={classnames(
                    styles["teleop-configuration"],
                    popup && styles.popup,
                )}
            >
                <Scrollable>
                    <Container className={styles.container}>
                        <VStack innerSpacing="lg">
                            <VStack innerSpacing="md">
                                <>
                                    <div
                                        className={classnames(
                                            styles["view-config"],
                                        )}
                                    >
                                        {[...Array(3).keys()].map(_ => (
                                            <TeleopViewSelectField
                                                key={`view${_}`}
                                                deviceIds={deviceIds || []}
                                                document={document}
                                                index={_}
                                                view={views[_]}
                                                label={
                                                    _ === 0
                                                        ? "Primary view"
                                                        : `Sidebar view ${_}`
                                                }
                                                onChangeView={this.onChangeView}
                                            />
                                        ))}
                                    </div>
                                    <Switch
                                        className={styles["arm-toggle"]}
                                        state={
                                            document.teleop?.armSwitch !== false
                                        }
                                        label="Control safety switch"
                                        onChange={this.onToggleArmSwitchConfig}
                                        info="Use the control safety switch to prevent accidental or unwanted input. When enabled, the operator must activate the controls by clicking the switch, or holding the space bar or L1/R1 on a connected gamepad"
                                        stateLabels={["disabled", "enabled"]}
                                    />
                                    <Switch
                                        className={styles["arm-toggle"]}
                                        state={
                                            document.teleop
                                                ?.allowLowBandwidthMode || false
                                        }
                                        label="Allow low bandwidth mode"
                                        onChange={
                                            this.onToggleAllowLowBandwidthMode
                                        }
                                        info="Allow operators to toggle low bandwidth mode for reduced video bandwidth in adverse network conditions"
                                        stateLabels={["disabled", "enabled"]}
                                    />
                                    <Switch
                                        className={styles["arm-toggle"]}
                                        state={
                                            document.teleop
                                                ?.disableHighPingWarning ||
                                            false
                                        }
                                        label="Disable high ping warning "
                                        onChange={
                                            this.onToggleDisableHighPingWarning
                                        }
                                        info="Disables the high ping warning popup that can sometimes obstruct the main view"
                                        stateLabels={["disabled", "enabled"]}
                                    />
                                </>
                            </VStack>
                        </VStack>
                    </Container>
                </Scrollable>
                <TabWorkflowButtons
                    dirty={dirty}
                    unsaved={unsaved}
                    onSave={onSave}
                    loading={loading}
                    saveCopy={saveCopy}
                />
            </Page>
        );
    }

    private onToggleArmSwitchConfig = (newState: boolean) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                armSwitch: newState,
            },
        });
    };

    private onToggleDisableHighPingWarning = (newState: boolean) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                disableHighPingWarning: newState,
            },
        });
    };

    private onToggleAllowLowBandwidthMode = (newState: boolean) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                allowLowBandwidthMode: newState,
            },
        });
    };

    private onChangeView = (index: number, view?: ITeleopViewConfiguration) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];

        views[index] = view || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
            },
        });
    };
}
